/** Shared create-post helpers (encoding matches titleFromPost / bodyAfterFirstLine). */

export const MAX_CONTENT_CHARS = 8000;
export const MIN_TITLE_CHARS = 3;
export const MAX_TITLE_CHARS = 300;
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** Max images per media-mode club post (composer + server). */
export const MAX_MEDIA_IMAGES_PER_POST = 10;
export const ACCEPT_ATTR = "image/jpeg,image/png,image/webp,image/gif";
export const ACCEPT_MIMES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

export function buildClubPostStoredContent(title, body) {
  const t = String(title || "").trim();
  const b = String(body || "").trim();
  if (!b) return t;
  return `${t}\n\n${b}`;
}

export function pickViewerId(user) {
  const keys = [
    user?.id,
    user?.user_id,
    user?.userId,
    user?.uid,
    user?.sub,
    user?.profile?.id,
    user?.profile?.user_id,
    user?.account?.id,
    user?.account?.user_id,
    user?.account_id,
  ];
  for (const v of keys) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function isActiveMember(membership) {
  if (!membership) return false;
  return String(membership.status || "").toLowerCase() === "active";
}

export function getUploadErrorMessage(err) {
  const nested = err?.data?.error;
  const fromNested =
    nested && typeof nested === "object" && nested.message != null
      ? String(nested.message)
      : typeof nested === "string"
        ? nested
        : "";
  return (
    fromNested ||
    err?.data?.message ||
    err?.data?.error ||
    err?.message ||
    "Upload failed."
  );
}

/**
 * Normalize club feed-media upload JSON (handles minor response-shape drift).
 * @param {unknown} body
 * @returns {{ kind: string, url: string, r2_key?: string | null, mime?: string | null, size_bytes?: number | null, width?: number | null, height?: number | null, duration_ms?: number | null, artwork_id?: number | null } | null}
 */
export function normalizeClubFeedUploadAttachment(body) {
  if (!body || typeof body !== "object") return null;
  const b = body;

  const nestedData =
    b.data && typeof b.data === "object"
      ? b.data.attachment != null
        ? b.data.attachment
        : typeof b.data.url === "string" || b.data.image_url != null
          ? b.data
          : null
      : null;

  const raw =
    (b.attachment && typeof b.attachment === "object" ? b.attachment : null) ??
    nestedData ??
    (b.result && typeof b.result === "object" ? b.result.attachment : null);

  let url = null;
  let kind = "image";
  let r2_key = null;
  let mime = null;
  let size_bytes = null;
  let width = null;
  let height = null;
  let duration_ms = null;
  let artwork_id = null;

  if (raw && typeof raw === "object") {
    url = raw.url ?? raw.image_url ?? raw.imageUrl ?? null;
    kind = String(raw.kind || "image").toLowerCase() || "image";
    r2_key = raw.r2_key != null ? String(raw.r2_key).trim() : null;
    mime = raw.mime != null ? String(raw.mime).trim() : null;
    size_bytes = raw.size_bytes != null ? Number(raw.size_bytes) : null;
    width = raw.width != null ? Number(raw.width) : null;
    height = raw.height != null ? Number(raw.height) : null;
    duration_ms = raw.duration_ms != null ? Number(raw.duration_ms) : null;
    artwork_id = raw.artwork_id != null ? Number(raw.artwork_id) : null;
  }

  if (url == null || String(url).trim() === "") {
    if (typeof b.url === "string" && b.url.trim() !== "") url = b.url.trim();
    else if (b.image_url != null && String(b.image_url).trim() !== "") url = String(b.image_url).trim();
    else if (b.imageUrl != null && String(b.imageUrl).trim() !== "") url = String(b.imageUrl).trim();
  }

  if (url == null || String(url).trim() === "") return null;

  return {
    kind,
    url: String(url).trim(),
    r2_key,
    mime,
    size_bytes: Number.isFinite(size_bytes) ? size_bytes : null,
    width: Number.isFinite(width) ? width : null,
    height: Number.isFinite(height) ? height : null,
    duration_ms: Number.isFinite(duration_ms) ? duration_ms : null,
    artwork_id: Number.isFinite(artwork_id) ? artwork_id : null,
  };
}

/** Payload row for createClubPost (no composer-only fields). */
export function clubComposerAttachmentForApi(att, sortOrder) {
  return {
    kind: String(att.kind || "image").toLowerCase(),
    url: String(att.url || "").trim(),
    r2_key: att.r2_key != null ? String(att.r2_key).trim() : null,
    mime: att.mime != null ? String(att.mime).trim() : null,
    size_bytes:
      att.size_bytes != null && Number.isFinite(Number(att.size_bytes))
        ? Math.trunc(Number(att.size_bytes))
        : null,
    width:
      att.width != null && Number.isFinite(Number(att.width)) ? Math.trunc(Number(att.width)) : null,
    height:
      att.height != null && Number.isFinite(Number(att.height))
        ? Math.trunc(Number(att.height))
        : null,
    duration_ms:
      att.duration_ms != null && Number.isFinite(Number(att.duration_ms))
        ? Math.trunc(Number(att.duration_ms))
        : null,
    artwork_id:
      att.artwork_id != null && Number.isFinite(Number(att.artwork_id))
        ? Math.trunc(Number(att.artwork_id))
        : null,
    sort_order: sortOrder,
  };
}

/**
 * Explains why Post is disabled (footer hint). Returns null when ready to submit.
 * Post tab: title required, body optional, no media. Media tab: title + image required, no body.
 * @param {{ title: string, contentLengthOk: boolean, hasMedia: boolean, busy: boolean, composerMode: 'post' | 'media' }} p
 */
export function describeComposerBlockedReason({
  title,
  contentLengthOk,
  hasMedia,
  busy,
  composerMode,
}) {
  if (busy) return null;
  const t = String(title || "").trim();
  const len = t.length;
  if (len === 0) return "Add a title — it becomes the thread title in the feed.";
  if (len < MIN_TITLE_CHARS) return `Title needs at least ${MIN_TITLE_CHARS} characters.`;
  if (len > MAX_TITLE_CHARS) return `Title must be ${MAX_TITLE_CHARS} characters or fewer.`;
  if (!contentLengthOk) {
    return composerMode === "media"
      ? "Shorten your title to stay within the limit."
      : "Shorten your title or body to stay within the limit.";
  }
  if (composerMode === "media") {
    if (!hasMedia) return "Add at least one image to publish.";
    return null;
  }
  return null;
}
